import React, { useState } from "react";

/**
 * Confirms a user answer that may start from a system-detected candidate
 * (currently only the Patient Narrative CIOMS subject-identifier field).
 *
 * `detectedValue` (system/mapping state) and `fieldValue`/`confirmed` (the
 * user's actual wizard answer) are kept as separate props and never
 * merged - see `patient_narrative_wizard_schema.py`'s module docstring for
 * why. The backend cannot currently tell "detection hasn't run" apart from
 * "detection ran and found nothing" (both are `detectedValue === null`), so
 * this deliberately renders neutral copy ("No detected field is available
 * yet") rather than a false claim like "no subject identifier was found" -
 * and it takes a plain text field for manual entry rather than a fake
 * dropdown, because no backend endpoint yet supplies real field-name
 * candidates to choose from. Once that endpoint exists, swapping this
 * text input for a real select is the only change needed here.
 */
const DetectedValueConfirmation = ({
  detectedValue = null,
  fieldValue = null,
  confirmed = false,
  onChangeField,
  onConfirmField,
  disabled = false,
}) => {
  const [isEditing, setIsEditing] = useState(!detectedValue && !fieldValue);
  const [draftValue, setDraftValue] = useState(
    fieldValue || detectedValue || ""
  );

  const startEditing = seedValue => {
    setDraftValue(seedValue || "");
    setIsEditing(true);
  };

  const confirmDraft = () => {
    onChangeField(draftValue);
    onConfirmField();
    setIsEditing(false);
  };

  if (confirmed && fieldValue && !isEditing) {
    return (
      <div className="detected-value-confirmation detected-value-confirmation--confirmed">
        <span className="detected-value-confirmation__value">
          Confirmed: {fieldValue}
        </span>
        <button
          type="button"
          className="input-submit input-submit__secondary input-submit__small"
          disabled={disabled}
          onClick={() => startEditing(fieldValue)}>
          Choose a different field
        </button>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="detected-value-confirmation detected-value-confirmation--editing">
        {!detectedValue && (
          <p className="detected-value-confirmation__neutral">
            No detected field is available yet.
          </p>
        )}
        <input
          type="text"
          className="input-text"
          value={draftValue}
          disabled={disabled}
          placeholder="Enter the subject identifier field name"
          onChange={e => setDraftValue(e.target.value)}
        />
        <button
          type="button"
          className="input-submit input-submit__tertiary input-submit__small"
          disabled={disabled || !draftValue}
          onClick={confirmDraft}>
          Confirm
        </button>
      </div>
    );
  }

  // detectedValue exists and hasn't been confirmed yet.
  return (
    <div className="detected-value-confirmation detected-value-confirmation--detected">
      <span className="detected-value-confirmation__value">
        Detected: {detectedValue}
      </span>
      <button
        type="button"
        className="input-submit input-submit__tertiary input-submit__small"
        disabled={disabled}
        onClick={() => {
          onChangeField(detectedValue);
          onConfirmField();
        }}>
        Confirm
      </button>
      <button
        type="button"
        className="input-submit input-submit__secondary input-submit__small"
        disabled={disabled}
        onClick={() => startEditing(detectedValue)}>
        Choose a different field
      </button>
    </div>
  );
};

export default DetectedValueConfirmation;
